// =============================================
// 3. UPDATED DETAILED REPORT WITH CORRECT SCHEMA SUPPORT
// components/reports/DetailedReport.js
// =============================================
import React, { useState, useMemo } from 'react'
import { ChevronDown, ChevronUp, Split, FileText } from 'lucide-react'
import { processTimeEntries } from '../../utils/timeSplitUtils'

export const DetailedReport = ({ data }) => {
  const [sortField, setSortField] = useState('entry_date')
  const [sortDirection, setSortDirection] = useState('desc')
  const [showSplitsExpanded, setShowSplitsExpanded] = useState(true)

  // Process entries to show splits
  const processedData = useMemo(() => {
    if (showSplitsExpanded) {
      return processTimeEntries(data)
    }
    return data.map(entry => ({ ...entry, is_split_entry: false }))
  }, [data, showSplitsExpanded])

  const sortedData = useMemo(() => {
    const sorted = [...processedData].sort((a, b) => {
      let aVal = a[sortField]
      let bVal = b[sortField]
      
      if (sortField === 'entry_date') {
        aVal = new Date(aVal)
        bVal = new Date(bVal)
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [processedData, sortField, sortDirection])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
    >
      <span>{children}</span>
      {sortField === field && (
        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
      )}
    </button>
  )

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      submitted: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      invoiced: 'bg-purple-100 text-purple-700'
    }
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.draft}`}>
        {status}
      </span>
    )
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">No Time Entries</h3>
        <p className="text-slate-600">No time entries found for the selected filters.</p>
      </div>
    )
  }

  const splitCount = processedData.filter(e => e.is_split_entry).length
  const splitEntriesCount = new Set(processedData.filter(e => e.is_split_entry).map(e => e.parent_entry_id)).size

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">
          All Time Entries ({sortedData.length} {sortedData.length === 1 ? 'entry' : 'entries'})
        </h3>
        
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={showSplitsExpanded}
              onChange={(e) => setShowSplitsExpanded(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Show split entries separately</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center bg-slate-50 border-b border-slate-200 text-left sticky top-0 z-10">
          <div className="flex-1 px-4 py-3">
            <SortButton field="entry_date">Date</SortButton>
          </div>
          <div className="flex-1 px-4 py-3">
            <SortButton field="user_name">User</SortButton>
          </div>
          <div className="flex-1 px-4 py-3">
            <SortButton field="client_name">Client</SortButton>
          </div>
          <div className="flex-1 px-4 py-3">
            <SortButton field="project_name">Project</SortButton>
          </div>
          <div className="flex-1 px-4 py-3">
            <SortButton field="task_name">Task</SortButton>
          </div>
          <div className="w-20 px-4 py-3 text-right">
            <SortButton field="duration_hours">Hours</SortButton>
          </div>
          <div className="w-24 px-4 py-3 text-right">
            <SortButton field="hourly_rate">Rate</SortButton>
          </div>
          <div className="w-16 px-4 py-3 text-center">
            <SortButton field="is_billable">Billable</SortButton>
          </div>
          <div className="w-24 px-4 py-3 text-right">Amount</div>
          <div className="w-20 px-4 py-3">
            <SortButton field="status">Status</SortButton>
          </div>
        </div>
        
        {/* Rows */}
        <div className="divide-y divide-slate-100">
          {sortedData.map((entry, index) => {
            const amount = entry.is_billable ? (entry.duration_hours * entry.hourly_rate) : 0
            const isEven = index % 2 === 0
            
            return (
              <div 
                key={entry.id}
                className={`flex items-center ${isEven ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50 transition-colors ${
                  entry.is_split_entry ? 'border-l-4 border-blue-400' : ''
                }`}
              >
                <div className="flex-1 px-4 py-3 text-sm text-slate-900">
                  {new Date(entry.entry_date).toLocaleDateString()}
                </div>
                <div className="flex-1 px-4 py-3 text-sm text-slate-900">
                  {entry.user_name || 'Unknown User'}
                </div>
                <div className="flex-1 px-4 py-3 text-sm text-slate-900">
                  {entry.client_name || 'No Client'}
                </div>
                <div className="flex-1 px-4 py-3">
                  <div className="flex items-center space-x-2">
                    {entry.project_color && (
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: entry.project_color }}
                      />
                    )}
                    <span className="text-sm text-slate-900">
                      {entry.project_name || 'No Project'}
                    </span>
                  </div>
                </div>
                <div className="flex-1 px-4 py-3">
                  <div className="flex items-center space-x-2">
                    {entry.is_split_entry && (
                      <Split className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    )}
                    <span className="text-sm text-slate-900">{entry.task_name}</span>
                  </div>
                </div>
                <div className="w-20 px-4 py-3 text-sm text-slate-900 text-right font-medium">
                  {entry.duration_hours.toFixed(2)}h
                </div>
                <div className="w-24 px-4 py-3 text-sm text-slate-600 text-right">
                  {formatCurrency(entry.hourly_rate)}
                </div>
                <div className="w-16 px-4 py-3 text-center">
                  {entry.is_billable ? (
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                      <span className="text-xs font-medium text-green-700">✓</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-slate-100 rounded-full">
                      <span className="text-xs font-medium text-slate-400">-</span>
                    </span>
                  )}
                </div>
                <div className="w-24 px-4 py-3 text-sm font-medium text-slate-900 text-right">
                  {formatCurrency(amount)}
                </div>
                <div className="w-20 px-4 py-3">
                  {getStatusBadge(entry.status)}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {processedData.length > 100 && (
        <div className="text-center text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
          Large dataset detected. Consider using filters to improve performance.
        </div>
      )}

      
    </div>
  )
}

export default DetailedReport